import {addCommasToNumber} from '../../../utils';
import {
    PROPERTY_FIELDS,
    NUMBER_FIELDS,
    TEXT_FIELDS,
    UNIT_MIX_FIELDS,
    emptyInputs,
    emptyUnitMixRow,
    emptyOtherIncomeRow,
} from './fields';
import {toNumber} from './calculate';

/**
 * Conversions between the popup's `inputs`, the underwriting_multi_family row, the
 * multi-tenant underwriting it replaces, and the PROPERTIES record.
 */

const blankToEmpty = value => (value === null || value === undefined ? '' : value);

function withPropertyFields(inputs, property) {
    PROPERTY_FIELDS.forEach(key => { inputs[key] = blankToEmpty(property[key]); });
    return inputs;
}

function rowsOrDefault(rows, fallback) {
    return rows && rows.length ? rows : fallback;
}

/** Inputs from a saved underwriting_multi_family row. */
export function fromApi(row, property) {
    const defaults = emptyInputs();
    const inputs = withPropertyFields({...defaults}, property);
    NUMBER_FIELDS.concat(TEXT_FIELDS).forEach(key => { inputs[key] = blankToEmpty(row[key]); });
    inputs.unit_mix = rowsOrDefault(row.unit_mix, defaults.unit_mix)
        .map(r => ({...emptyUnitMixRow(), ...stripTypename(r)}))
        .map(r => mapValues(r, blankToEmpty));
    ['current_other_income', 'proforma_other_income'].forEach(key => {
        inputs[key] = rowsOrDefault(row[key], defaults[key])
            .map(r => ({...emptyOtherIncomeRow(), ...stripTypename(r)}))
            .map(r => mapValues(r, blankToEmpty));
    });
    return inputs;
}

/**
 * Inputs for a property opened in this popup for the first time: whatever the multi-tenant
 * underwriting it was using before already holds. Only fields that mean the same thing in
 * both are copied - the multi-tenant management fee, for one, is a dollar amount rather than
 * a share of EGI, so it is left blank.
 */
export function fromMultiTenant(mt, property) {
    const inputs = withPropertyFields(emptyInputs(), property);
    if (property.capitalization_rate) inputs.cap_rate = toNumber(property.capitalization_rate);
    if (!mt) return inputs;

    const copy = (from, to = from) => {
        if (mt[from] !== null && mt[from] !== undefined && mt[from] !== '') inputs[to] = mt[from];
    };
    // Lot size lives on the property record; the old underwriting only fills it when that is empty.
    if (inputs.lot_size === '') copy('lot_size');
    copy('current_tax_appraised_value');
    copy('google_maps_link');
    copy('cad_link_1', 'cad_link');
    copy('analysis_date');
    copy('down_payment_percent');
    copy('interest_rate');
    copy('amortization');
    copy('current_insurance', 'insurance');
    copy('proforma_insurance');
    copy('expenses_notes', 'expense_notes');
    copy('pricing_bps_increase', 'bps_change');

    // The multi-tenant screen stores the tax liability, not the rate; recover the rate so
    // Real Estate Taxes (appraised value x mill rate) comes out the same.
    const appraised = toNumber(mt.current_tax_appraised_value);
    const liability = toNumber(mt.current_tax_liability);
    if (appraised && liability) inputs.mill_rate = Number((liability / appraised * 100).toFixed(6));

    NUMBER_FIELDS.concat('lot_size', 'parking_spaces').forEach(key => {
        if (inputs[key] !== '' && inputs[key] !== null && inputs[key] !== undefined) inputs[key] = toNumber(inputs[key]);
    });
    return inputs;
}

/** The mutation input for createOrUpdateUnderwritingMultiFamily. */
export function toApi(inputs, propertyId) {
    const payload = {property_id: Number(propertyId)};
    NUMBER_FIELDS.forEach(key => {
        payload[key] = inputs[key] === '' || inputs[key] === null ? null : toNumber(inputs[key]);
    });
    TEXT_FIELDS.forEach(key => {
        const value = String(blankToEmpty(inputs[key])).trim();
        payload[key] = value || null;
    });

    payload.unit_mix = inputs.unit_mix
        .filter(row => !isBlankRow(row))
        .map(row => {
            const out = {floor_plan: String(row.floor_plan || '').trim()};
            UNIT_MIX_FIELDS.filter(key => key !== 'floor_plan').forEach(key => {
                out[key] = row[key] === '' ? null : toNumber(row[key]);
            });
            return out;
        });
    ['current_other_income', 'proforma_other_income'].forEach(key => {
        payload[key] = inputs[key]
            .filter(row => !isBlankRow(row))
            .map(row => ({
                category: String(row.category || '').trim(),
                amount: row.amount === '' ? null : toNumber(row.amount),
            }));
    });
    return payload;
}

/**
 * The PROPERTIES record after a save: the property fields edited here, plus the headline
 * figures the Financial tab and listings show, so they agree with the underwriting.
 * Figures that come out as zero leave the record's value alone, as the multi-tenant save does.
 */
export function toProperty(property, inputs, calc) {
    const next = {...property};
    PROPERTY_FIELDS.forEach(key => { next[key] = String(blankToEmpty(inputs[key])).trim(); });
    // PROPERTIES.parking_spaces is an INTEGER column.
    if (next.parking_spaces !== '') next.parking_spaces = String(Math.round(toNumber(next.parking_spaces)));

    const current = calc.valuation.current;
    const proforma = calc.valuation.proforma;
    if (calc.units > 0) next.units = String(calc.units);
    if (calc.rentableSf > 0) next.sqft = addCommasToNumber(Math.round(calc.rentableSf));
    if (current.noi > 0) next.net_operating_income = addCommasToNumber(Math.round(current.noi));
    if (current.price > 0) next.list_price = addCommasToNumber(Math.round(current.price));
    if (current.cap_rate > 0) next.capitalization_rate = roundPercent(current.cap_rate);
    if (proforma.noi > 0) next.proforma_noi = String(Math.round(proforma.noi));
    if (proforma.cap_rate > 0) next.proforma_cap_rate = roundPercent(proforma.cap_rate);
    // PROPERTIES.vacancy holds the occupancy percentage (PropertyFinancials labels it "Occupancy").
    if (calc.units > 0) next.vacancy = Math.round(calc.rentRoll.occupancy * 100);
    return next;
}

/** True when anything on the property record would change. */
export function propertyChanged(before, after) {
    return Object.keys(after).some(key => String(blankToEmpty(before[key])) !== String(blankToEmpty(after[key])));
}

function roundPercent(decimal) {
    return String(Number((decimal * 100).toFixed(4)));
}

function isBlankRow(row) {
    return Object.keys(row).every(key => String(blankToEmpty(row[key])).trim() === '');
}

/** Drops Apollo's __typename so a loaded row can be sent back as mutation input. */
function stripTypename(row) {
    const rest = {...(row || {})};
    delete rest.__typename;
    return rest;
}

function mapValues(object, fn) {
    const out = {};
    Object.keys(object).forEach(key => { out[key] = fn(object[key]); });
    return out;
}
